import { useEffect, useRef, useState } from "react";
import {
  Instagram,
  Facebook,
  Linkedin,
  Phone,
  Mail,
  MapPin,
  Clock,
  Lock,
  Coffee,
} from "lucide-react";
import { useReveal, Filo } from "./motion";
import { PRODUCTS } from "../../data/productData";
import { BUSINESS_TYPES } from "../../data/businessTypes";

const NAV_LINKS = [
  ["Sortiment", "sortiment"],
  ["Beviset", "crema"],
  ["Arven", "arven"],
  ["Til erhverv", "erhverv"],
  ["Kontakt", "kontakt"],
];

const go = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

/* Il Filo — the same thread, weaving left then right down the page.
   Each section carries one segment; together they read as one line
   running from the roastery down to the call to action. */
const FILO_LEFT = "M70 0 C70 66, 16 88, 70 148 C118 202, 70 196, 70 232";
const FILO_RIGHT = "M70 0 C70 66, 124 88, 70 148 C22 202, 70 196, 70 232";
const filoProps = (dir, dot) => ({
  d: dir === "left" ? FILO_LEFT : FILO_RIGHT,
  width: 140,
  height: 232,
  top: -132,
  dot: dot ? [70, 232] : null,
});

/* ════════════════════════════ HEADER ════════════════════════════ */
/* onNavItem lets a sub-page (the catalogue) send nav clicks home instead of
   scrolling to anchors that only exist on the landing page. */
export function Header({ onLogin, onNavItem }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    if (!open) return;
    const esc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [open]);

  const jump = (id) => {
    if (onNavItem) onNavItem(id);
    else go(id);
    setOpen(false);
  };

  return (
    <>
      <header className={`hdr ${scrolled ? "up" : ""}`}>
        <a
          className="logo"
          href="#main"
          onClick={(e) => {
            e.preventDefault();
            if (onNavItem) onNavItem(null);
            else window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        >
          <span className="logo-name">SCANDIVIA FOOD GROUP</span>
          <span className="logo-tag">Officiel distributør · Amigos Caffè</span>
        </a>

        <nav className="nav-links" aria-label="Hovedmenu">
          {NAV_LINKS.map(([label, id]) => (
            <a
              key={id}
              href={`#${id}`}
              onClick={(e) => {
                e.preventDefault();
                jump(id);
              }}
            >
              {label}
            </a>
          ))}
        </nav>

        <a className="hdr-cta" href="/login" onClick={onLogin}>
          Log ind
        </a>

        <button
          className={`burger ${open ? "open" : ""}`}
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Luk menu" : "Åbn menu"}
          aria-expanded={open}
          aria-controls="mobile-nav"
        >
          <span />
          <span />
          <span />
        </button>
      </header>

      <div id="mobile-nav" className={`mobile-nav ${open ? "open" : ""}`} aria-hidden={!open}>
        {NAV_LINKS.map(([label, id]) => (
          <a
            key={id}
            href={`#${id}`}
            onClick={(e) => {
              e.preventDefault();
              jump(id);
            }}
          >
            {label}
          </a>
        ))}
        <button className="btn btn-gold" onClick={() => jump("kontakt")}>
          Få smagsprøver
        </button>
      </div>
    </>
  );
}

/* ════════════════════════════ MARQUEE ════════════════════════════ */
const TICKER = [
  "Ristet i Trieste siden 1980",
  "Officiel dansk distributør",
  "Ingen minimumsordre",
  "Levering 1-3 hverdage",
  "Arabica og robusta",
  "Smagsprøver til nye samarbejder",
];

export function Ticker() {
  return (
    <div className="marquee" aria-hidden="true">
      <div className="marquee-inner">
        {[0, 1].map((set) => (
          <div className="marquee-set" key={set}>
            {TICKER.map((t, i) => (
              <span className="m-item" key={i}>
                {t}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ════════════════════════════ BLENDS ════════════════════════════ */
const LANDING_IDS = [1, 7, 3]; // Il Mingardi S, 7 Origini, Qualità Extra Bar
const LANDING_PRODUCTS = LANDING_IDS.map((id) =>
  PRODUCTS.find((p) => p.id === id),
).filter(Boolean);

/* the source data carries a stray double percent on one blend */
const cleanComposition = (s) => (s || "").replace("%%", "%");

export function Blends({ onCatalog }) {
  const rv = useReveal();

  return (
    <section id="sortiment" className="sec blends">
      <Filo {...filoProps("left")} />
      <div className="wrap">
        <div className="blends-head">
          <div ref={rv} className="rv">
            <p className="eyebrow">Sortiment · Amigos Caffè</p>
            <h2 className="sec-h">
              Forskellige blandinger
              <br />
              <em>ristet med passion</em>
            </h2>
            <p className="lede">
              Ni blandinger deler den samme ristning i Trieste. Forskellen
              ligger i bønnen, ikke i standarden.
            </p>
          </div>
          <button ref={rv} className="rv d1 btn btn-ink" onClick={onCatalog}>
            Se fuldt katalog
          </button>
        </div>

        <div className="blend-grid">
          {LANDING_PRODUCTS.map((p, i) => (
            <button
              key={p.id}
              ref={rv}
              className={`rv d${i + 1} bcard`}
              onClick={onCatalog}
              aria-label={`${p.title}, ${cleanComposition(p.composition)}. Se produkt i kataloget`}
            >
              <div className="bcard-media">
                <div className="bcard-glow" aria-hidden="true" />
                <span className="bcard-no" aria-hidden="true">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="bcard-tag">{cleanComposition(p.composition)}</span>
                <img
                  className="bcard-img"
                  src={p.img}
                  alt={`${p.title}, kaffebønner fra Amigos Caffè`}
                  loading={i === 0 ? "eager" : "lazy"}
                />
              </div>
              <div className="bcard-body">
                <div className="bcard-origin">
                  {p.origin} · {p.roast} ristning
                </div>
                <div className="bcard-name">{p.title}</div>
                <p className="bcard-desc">{p.description}</p>
                <div className="bcard-foot">
                  <span className="bcard-weight">{p.weight}</span>
                  <span className="bcard-view">
                    Se produkt
                    <span className="bcard-arrow" aria-hidden="true">
                      →
                    </span>
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════ CREMA — the interactive moment ════════════════════ */
const NOTES = [
  ["Aroma", 4],
  ["Fylde", 4],
  ["Sødme", 5],
  ["Balance", 5],
];

export function Crema() {
  const rv = useReveal();
  const [p, setP] = useState(0);
  const [done, setDone] = useState(false);
  const [holding, setHolding] = useState(false);
  const raf = useRef(null);
  const hold = useRef(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const pin = (e) => {
      if (e.matches !== false) {
        setP(1);
        setDone(true);
      }
    };
    if (reduce.matches) {
      setP(1);
      setDone(true);
    }
    reduce.addEventListener("change", pin);
    return () => reduce.removeEventListener("change", pin);
  }, []);

  useEffect(() => {
    let last = 0;
    const loop = (now) => {
      const dt = Math.min(50, now - (last || now));
      last = now;
      setP((prev) => {
        const next = hold.current
          ? Math.min(1, prev + dt / 1600)
          : Math.max(0, prev - dt / 900);
        if (next >= 1) setDone(true);
        if ((hold.current && next < 1) || (!hold.current && next > 0)) {
          raf.current = requestAnimationFrame(loop);
        } else {
          raf.current = null;
          last = 0;
        }
        return next;
      });
    };
    const start = () => {
      if (raf.current === null || raf.current === undefined) {
        last = 0;
        raf.current = requestAnimationFrame(loop);
      }
    };
    window.__cremaStart = start;
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, []);

  const down = (e) => {
    e.preventDefault();
    hold.current = true;
    setHolding(true);
    window.__cremaStart && window.__cremaStart();
  };
  const up = () => {
    hold.current = false;
    setHolding(false);
    window.__cremaStart && window.__cremaStart();
  };

  const lit = (i) => done || p > (i + 1) / (NOTES.length + 1);

  return (
    <section id="crema" className="sec crema">
      <Filo {...filoProps("right")} />
      <div className="wrap">
        <div className="crema-grid">
          <div ref={rv} className="rv">
            <p className="eyebrow">Beviset</p>
            <h2 className="sec-h">
              Hold inde, og
              <br />
              <em>træk en espresso</em>
            </h2>
            <p className="lede" style={{ marginTop: 26 }}>
              Cremaen bygger sig op over 25 sekunder. Er den gylden, tæt og
              bliver den liggende, er bønnen frisk og ristningen rigtig. Det er
              den eneste test der tæller bag disken.
            </p>

            <div className="crema-notes">
              {NOTES.map(([label, score], i) => (
                <div key={label} className={`cnote ${lit(i) ? "lit" : ""}`}>
                  <span className="cnote-i">{String(i + 1).padStart(2, "0")}</span>
                  <span className="cnote-l">{label}</span>
                  <span className="cnote-bar" aria-label={`${score} af 5`}>
                    {[0, 1, 2, 3, 4].map((n) => (
                      <i key={n} className={lit(i) && n < score ? "on" : ""} />
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div ref={rv} className="rv d1">
            <div className="crema-cup" style={{ "--p": p.toFixed(3) }}>
              <svg viewBox="0 0 200 200" role="img" aria-label="Espressokop der fyldes med crema">
                <defs>
                  <linearGradient id="cremaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#dbc992" />
                    <stop offset="55%" stopColor="#bfa46a" />
                    <stop offset="100%" stopColor="#4a3418" />
                  </linearGradient>
                  <clipPath id="cupClip">
                    <path d="M52 78 H148 L138 128 A38 30 0 0 1 62 128 Z" />
                  </clipPath>
                </defs>

                <circle className="crema-ring-track" cx="100" cy="100" r="85" />
                <circle className="crema-ring-fill" cx="100" cy="100" r="85" />

                <g clipPath="url(#cupClip)">
                  <rect
                    className="crema-liquid"
                    x="50"
                    y="76"
                    width="100"
                    height="82"
                  />
                </g>

                {/* cup body, handle, saucer */}
                <path
                  className="crema-cupline"
                  d="M52 78 H148 L138 128 A38 30 0 0 1 62 128 Z"
                />
                <path className="crema-cupline" d="M149 88 C176 88 176 122 143 121" />
                <path className="crema-saucer" d="M46 156 H154" />
                <ellipse className="crema-saucer" cx="100" cy="156" rx="46" ry="6" />

                <path className="crema-steam" d="M88 66 c-7 -9 7 -15 0 -25" />
                <path className="crema-steam" d="M112 66 c-7 -9 7 -15 0 -25" />
              </svg>

              <div className="crema-hold">
                <button
                  className={`hold-btn ${holding ? "holding" : ""} ${done ? "done" : ""}`}
                  onPointerDown={down}
                  onPointerUp={up}
                  onPointerLeave={up}
                  onPointerCancel={up}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") down(e);
                  }}
                  onKeyUp={up}
                  aria-pressed={holding}
                >
                  <span className="hold-dot" aria-hidden="true" />
                  {done ? "Perfekt udtræk" : "Hold inde"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════ HERITAGE ════════════════════════════ */
export function Heritage() {
  const rv = useReveal();
  const motes = [
    [12, 62, 0],
    [26, 78, -6],
    [41, 54, -12],
    [58, 84, -3],
    [72, 66, -15],
    [86, 74, -9],
  ];

  return (
    <section id="arven" className="heritage">
      <div
        className="heritage-bg"
        style={{ backgroundImage: "url('/assets/General/coffee-roasting.jpg')" }}
        aria-hidden="true"
      />
      <div className="heritage-veil" aria-hidden="true" />
      {motes.map(([l, t, d], i) => (
        <span
          key={i}
          className="mote"
          aria-hidden="true"
          style={{ left: `${l}%`, top: `${t}%`, animationDelay: `${d}s` }}
        />
      ))}

      <div className="wrap">
        <div className="heritage-grid">
          <div ref={rv} className="rv">
            <p className="eyebrow on-dark">Arven</p>
            <h2 className="sec-h on-dark">
              Fra Triestes hjerte
              <br />
              <em>til det danske marked</em>
            </h2>
            <p>
              Scandivia Food Group er en familieejet virksomhed og officiel
              distributør i Danmark for <strong>Amigos Caffè</strong>, et
              traditionsrigt risteri fra Trieste, grundlagt af familien Mingardi
              i 1980.
            </p>
            <p>
              Bag hver kaffeblanding ligger tre generationers passion, erfaring
              og kærlighed til ægte italiensk kaffehåndværk. Som familie&shy;virksomhed
              deler vi de samme værdier: kvalitet, tillid og langsigtede
              relationer.
            </p>
            <p>
              Vi gør det enkelt for danske caféer, restauranter, hoteller og
              virksomheder at servere kaffe i verdensklasse, med produkter,
              rådgivning og service der gør en forskel i hver eneste kop.
            </p>
          </div>

          <div ref={rv} className="rv d1 h-plaque">
            <div className="h-plaque-year">1980</div>
            <div className="h-plaque-city">Trieste</div>
            <div className="h-plaque-sub">La città del caffè · Italien</div>
            <div className="h-plaque-rule" />
            <div className="h-stats">
              {[
                ["1980", "Amigos grundlagt"],
                ["11+", "Blandinger"],
                ["1.", "Danske distributør"],
                ["Muggia", "Trieste, Italien"],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="h-stat-n">{n}</div>
                  <div className="h-stat-l">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════ WHY ════════════════════════════ */
const WHY = [
  [
    "Kvalitet der kan gentages",
    "Samme blanding, samme ristning, samme kop. Din barista skal ikke justere kværnen om, fordi leverandøren har skiftet bønne.",
  ],
  [
    "Italiensk håndværk",
    "Tre generationer af familien Mingardi har ristet i Trieste siden 1980. Byen har levet af kaffe i tre hundrede år.",
  ],
  [
    "Rådgivning, ikke bare levering",
    "Vi hjælper med at vælge ristning og dosering til jeres maskiner og jeres gæster. Det er en del af aftalen.",
  ],
  [
    "Ingen binding",
    "Ingen minimumsordre og ingen lang kontrakt. I justerer mængder fra måned til måned efter det faktiske forbrug.",
  ],
];

export function Why() {
  const rv = useReveal();
  return (
    <section className="sec why">
      <Filo {...filoProps("left")} />
      <div className="wrap">
        <div ref={rv} className="rv why-head">
          <p className="eyebrow">Derfor denne kaffe</p>
          <h2 className="sec-h">
            Fire grunde til
            <br />
            <em>at skifte</em>
          </h2>
        </div>
        {WHY.map(([h, p], i) => (
          <div key={h} ref={rv} className={`rv d${Math.min(i + 1, 4)} why-row`}>
            <span className="why-n">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="why-h">{h}</h3>
            <p className="why-p">{p}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ════════════════════════════ B2B ════════════════════════════ */
const STEPS = [
  [
    "Smagsprøver",
    "Fortæl os hvad I serverer i dag. Vi sender prøver på de blandinger der passer.",
  ],
  [
    "Vi justerer sammen",
    "Ristning, dosering og maling tilpasses jeres maskiner, inden I bestiller for alvor.",
  ],
  [
    "Fast levering",
    "Sjælland og Fyn 1-2 hverdage, Jylland 2-3. Bestil inden kl. 12, så afsender vi samme dag.",
  ],
];

const SERVE = BUSINESS_TYPES.filter((t) => t !== "Andet");

export function B2B() {
  const rv = useReveal();
  return (
    <section id="erhverv" className="sec b2b">
      <Filo {...filoProps("right")} />
      <div className="wrap">
        <div ref={rv} className="rv b2b-head">
          <p className="eyebrow">Til erhverv</p>
          <h2 className="sec-h">
            Kaffe til dem der
            <br />
            <em>serverer den videre</em>
          </h2>
        </div>

        <div ref={rv} className="rv d1 b2b-pills">
          {SERVE.map((t) => (
            <span className="b2b-pill" key={t}>
              {t}
            </span>
          ))}
        </div>

        <div className="b2b-steps">
          {STEPS.map(([h, p], i) => (
            <div key={h} ref={rv} className={`rv d${i + 1} step`}>
              <div className="step-n">Trin {i + 1}</div>
              <h3 className="step-h">{h}</h3>
              <p className="step-p">{p}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════ FAQ ════════════════════════════ */
const FAQS = [
  {
    q: "Hvad er minimumsordren?",
    a: "Ingen fast minimumsordre. Som Starter begynder du fra blot 5 kg/md. Jo mere du bestiller, jo bedre vilkår og priser.",
  },
  {
    q: "Hvor hurtigt leveres der til Danmark?",
    a: "Sjælland og Fyn: 1-2 hverdage. Jylland: 2-3 hverdage. Ordrer modtaget inden kl. 12 afsender vi samme dag.",
  },
  {
    q: "Tilbyder I prøvesmagning?",
    a: "Ja. Nye samarbejdspartnere kan få tilsendt smagsprøver, så I kan teste kvaliteten før I bestiller større mængder.",
  },
  {
    q: "Kan vi ændre vores ordre fra måned til måned?",
    a: "Ja. Du kan justere mængder eller produkter løbende, så det passer til dit faktiske forbrug.",
  },
  {
    q: "Hvilke kaffetyper tilbyder I?",
    a: "Vi tilbyder både Arabica, Robusta og forskellige blends, så du kan vælge den smagsprofil der passer bedst til din virksomhed eller café.",
  },
  {
    q: "Tilbyder I kaffe til kontorer og virksomheder?",
    a: "Ja. Vi leverer kaffe til både kontorer, caféer, restauranter og detailbutikker.",
  },
  {
    q: "Kan I hjælpe med valg af kaffe?",
    a: "Ja. Vi rådgiver gerne om hvilken kaffe og ristning der passer bedst til jeres maskiner og kunder.",
  },
];

export function Faq() {
  const rv = useReveal();
  const [open, setOpen] = useState(null);
  const [heights, setHeights] = useState({});
  const bodies = useRef([]);

  /* Measure each answer once laid out, and again on resize, so an open
     row is never clipped and never over-tall on a reflow. */
  useEffect(() => {
    const measure = () => {
      const next = {};
      bodies.current.forEach((el, i) => {
        if (el) next[i] = el.scrollHeight;
      });
      setHeights(next);
    };
    measure();
    const ro = new ResizeObserver(measure);
    bodies.current.forEach((el) => el && ro.observe(el));
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  return (
    <section id="faq" className="sec faq">
      <div className="wrap">
        <div ref={rv} className="rv faq-head">
          <p className="eyebrow">FAQ</p>
          <h2 className="sec-h">
            Dine <em>spørgsmål</em>
          </h2>
        </div>
        <div className="faq-list">
          {FAQS.map((f, i) => {
            const on = open === i;
            return (
              <div className="faq-row" key={i} ref={rv}>
                <button
                  className="faq-q"
                  id={`faq-q-${i}`}
                  aria-expanded={on}
                  aria-controls={`faq-a-${i}`}
                  onClick={() => setOpen(on ? null : i)}
                >
                  <span className="faq-i">{String(i + 1).padStart(2, "0")}</span>
                  <span className="faq-t">{f.q}</span>
                  <span className={`faq-x ${on ? "on" : ""}`} aria-hidden="true" />
                </button>
                <div
                  className={`faq-a ${on ? "on" : ""}`}
                  id={`faq-a-${i}`}
                  role="region"
                  aria-labelledby={`faq-q-${i}`}
                  ref={(el) => (bodies.current[i] = el)}
                  style={{ maxHeight: on ? `${heights[i] || 320}px` : 0 }}
                >
                  <p>{f.a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════ CONTACT ════════════════════════════ */
export function Contact({ submit }) {
  const rv = useReveal();
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    navn: "",
    firma: "",
    email: "",
    tlf: "",
    type: "",
    message: "",
  });

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const send = async (e) => {
    e.preventDefault();
    if (!form.navn || !form.email) return;
    try {
      setLoading(true);
      setError(null);
      if (submit) await submit(form);
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="contact">
      <Filo {...filoProps("left", true)} />
      <div className="contact-glow" aria-hidden="true" />
      <div className="wrap">
        <div className="contact-grid">
          <div ref={rv} className="rv">
            <p className="eyebrow on-dark">Kontakt</p>
            <h2 className="contact-title">
              Lad os tage en
              <br />
              <em>snak om kaffe</em>
            </h2>
            <p className="contact-blurb">
              Udfyld formularen, så kontakter vi dig inden for én hverdag. Vi
              sender gerne smagsprøver med det samme.
            </p>

            <a className="c-det" href="tel:+4557202096">
              <span className="c-det-icon">
                <Phone size={15} strokeWidth={1.75} />
              </span>
              <span>
                <span className="c-det-label">Telefon</span>
                <span className="c-det-val">+45 57 20 20 96</span>
              </span>
            </a>
            <a className="c-det" href="mailto:info@scandiviafood.dk">
              <span className="c-det-icon">
                <Mail size={15} strokeWidth={1.75} />
              </span>
              <span>
                <span className="c-det-label">E-mail</span>
                <span className="c-det-val">info@scandiviafood.dk</span>
              </span>
            </a>
            <div className="c-det">
              <span className="c-det-icon">
                <MapPin size={15} strokeWidth={1.75} />
              </span>
              <span>
                <span className="c-det-label">Adresse</span>
                <span className="c-det-val">Yderholmen 15, 2750 Ballerup</span>
              </span>
            </div>
            <div className="c-det">
              <span className="c-det-icon">
                <Clock size={15} strokeWidth={1.75} />
              </span>
              <span>
                <span className="c-det-label">Åbningstider</span>
                <span className="c-det-val">Man-fre 8:00-17:00</span>
              </span>
            </div>
          </div>

          <div ref={rv} className="rv d1 contact-card">
            {done ? (
              <div className="done-wrap">
                <span className="done-icon">
                  <Coffee size={26} strokeWidth={1.5} />
                </span>
                <h3 className="done-h">Tak, {form.navn}!</h3>
                <p className="done-p">Vi vender tilbage inden for én hverdag.</p>
              </div>
            ) : (
              <form onSubmit={send}>
                <div className="f-row">
                  <div className="f-group">
                    <label className="f-label" htmlFor="cf-navn">
                      Navn *
                    </label>
                    <input
                      id="cf-navn"
                      required
                      className="f-input"
                      placeholder="Dit navn"
                      value={form.navn}
                      onChange={upd("navn")}
                    />
                  </div>
                  <div className="f-group">
                    <label className="f-label" htmlFor="cf-firma">
                      Virksomhed
                    </label>
                    <input
                      id="cf-firma"
                      className="f-input"
                      placeholder="Firmanavn"
                      value={form.firma}
                      onChange={upd("firma")}
                    />
                  </div>
                </div>

                <div className="f-row">
                  <div className="f-group">
                    <label className="f-label" htmlFor="cf-email">
                      E-mail *
                    </label>
                    <input
                      id="cf-email"
                      required
                      type="email"
                      className="f-input"
                      placeholder="din@email.dk"
                      value={form.email}
                      onChange={upd("email")}
                    />
                  </div>
                  <div className="f-group">
                    <label className="f-label" htmlFor="cf-tlf">
                      Telefon
                    </label>
                    <input
                      id="cf-tlf"
                      type="tel"
                      className="f-input"
                      placeholder="+45 XX XX XX XX"
                      value={form.tlf}
                      onChange={upd("tlf")}
                    />
                  </div>
                </div>

                <div className="f-group">
                  <label className="f-label" htmlFor="cf-type">
                    Type forretning
                  </label>
                  <select
                    id="cf-type"
                    className="f-select"
                    value={form.type}
                    onChange={upd("type")}
                  >
                    <option value="">Vælg...</option>
                    {BUSINESS_TYPES.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>

                <div className="f-group">
                  <label className="f-label" htmlFor="cf-msg">
                    Besked
                  </label>
                  <textarea
                    id="cf-msg"
                    required
                    rows={3}
                    className="f-input"
                    placeholder="Fortæl kort om jeres kaffeønsker..."
                    value={form.message}
                    onChange={upd("message")}
                    style={{ resize: "none" }}
                  />
                </div>

                <button className="f-submit" type="submit" disabled={loading}>
                  {loading ? "Sender..." : "Send forespørgsel"}
                </button>

                {error && <p className="f-error">{error}</p>}

                <p className="f-note">
                  <Lock size={11} strokeWidth={1.75} />
                  Dine oplysninger deles aldrig med tredjepart
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════ FOOTER ════════════════════════════ */
export function Footer({ Link }) {
  const A = Link || (({ to, ...rest }) => <a href={to} {...rest} />);
  return (
    <footer>
      <div className="footer-top">
        <div className="wrap footer-grid">
          <div className="footer-col">
            <span className="footer-brand-name">SCANDIVIA FOOD GROUP</span>
            <p className="footer-tagline">
              Officiel dansk distributør af Amigos Caffè, ristet i Trieste siden
              1980. Premium italiensk kaffe til caféer, restauranter og
              virksomheder.
            </p>
            <div className="footer-social">
              <a
                href="https://www.instagram.com/scandiviafoodgroup"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <Instagram size={16} strokeWidth={1.5} />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61579652631350"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <Facebook size={16} strokeWidth={1.5} />
              </a>
              <a
                href="https://www.linkedin.com/company/scandiviafoodgroup/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <Linkedin size={16} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div className="footer-col">
            <p className="footer-heading">Navigation</p>
            {NAV_LINKS.map(([label, id]) => (
              <button
                key={id}
                type="button"
                className="footer-link"
                onClick={() => go(id)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="footer-col">
            <p className="footer-heading">Kontakt</p>
            <a href="tel:+4557202096" className="footer-contact-item">
              <Phone size={14} strokeWidth={1.75} />
              <span>+45 57 20 20 96</span>
            </a>
            <a href="mailto:info@scandiviafood.dk" className="footer-contact-item">
              <Mail size={14} strokeWidth={1.75} />
              <span>info@scandiviafood.dk</span>
            </a>
            <span className="footer-contact-item">
              <MapPin size={14} strokeWidth={1.75} />
              <span>Yderholmen 15, 2750 Ballerup</span>
            </span>
          </div>

          <div className="footer-col">
            <p className="footer-heading">Juridisk</p>
            <A to="/privacy-policy" className="footer-link">
              Privatlivspolitik
            </A>
            <A to="/cookies-policy" className="footer-link">
              Cookiepolitik
            </A>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="wrap footer-bottom-inner">
          <span className="f-copy">
            © {new Date().getFullYear()} Scandivia Food Group · CVR 46398610 ·
            Ballerup, Danmark
          </span>
          <a
            href="https://www.findsmiley.dk/Sider/Search.aspx?k=Scandivia%20Food%20ApS"
            target="_blank"
            rel="noopener noreferrer"
            className="footer-smiley"
            title="Gå til vores kontrolrapport på findsmiley.dk"
          >
            <img
              src="/assets/General/Banner_yellow.gif"
              alt="Findsmiley kontrolrapport"
              loading="lazy"
            />
          </a>
          <span className="f-brand">Importeret fra Trieste med passion</span>
        </div>
      </div>
    </footer>
  );
}
